import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, XCircle, Edit, Trash2, Eye, User, Building2, Mail, Phone, MapPin, ExternalLink, Calendar, Loader2, Globe, Linkedin, Twitter, Github, Instagram, Youtube, Facebook, MessageCircle, MoreVertical, UserPlus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const ReviewCards = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [processing, setProcessing] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignEmail, setAssignEmail] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalContacts, setTotalContacts] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const itemsPerPage = 10;

  // Hardcoded allowed emails for Review Cards access
  const ALLOWED_REVIEWER_EMAILS = [
    'sagarshiva111555@gmail.com',
    'allgrowtechonologies@gmail.com'
  ];

  // Check if current user has access
  const hasAccess = user?.email && ALLOWED_REVIEWER_EMAILS.includes(user.email.toLowerCase());
  
  // Check if current user is admin (only admin can assign cards)
  const isAdmin = user?.email && ALLOWED_REVIEWER_EMAILS.includes(user.email.toLowerCase());

  // Check if user has access to Review Cards page
  useEffect(() => {
    if (!user?.email) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to access this page.",
        variant: "destructive",
      });
      navigate('/');
      return;
    }
    
    if (!hasAccess) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to access this page.",
        variant: "destructive",
      });
      navigate('/');
    }
  }, [user, hasAccess, navigate, toast]);

  useEffect(() => {
    if (hasAccess) {
      fetchContacts();
    }
  }, [hasAccess]);
  
  // Reload contacts when page or search changes (with debounce for search)
  useEffect(() => {
    if (!hasAccess) return;
    
    const timer = setTimeout(() => {
      fetchContacts(currentPage, searchQuery);
    }, searchQuery ? 500 : 0); // 500ms debounce for search, immediate for page change
    
    return () => clearTimeout(timer);
  }, [currentPage, searchQuery, hasAccess]);

  const fetchContacts = async (page = currentPage, search = searchQuery) => {
    setLoading(true);
    try {
      const { scannedContactAPI } = await import('../services/api');
      const response = await scannedContactAPI.getAllForReview(page, itemsPerPage, search);
      
      // Response structure: { data, total, page, limit, totalPages }
      setContacts(response.data || []);
      setTotalContacts(response.total || 0);
      setTotalPages(response.totalPages || 0);
      setCurrentPage(response.page || 1);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to fetch contacts for review",
        variant: "destructive",
      });
      setContacts([]);
      setTotalContacts(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };
  
  // Search handler
  const handleSearchChange = (e) => {
    const newSearch = e.target.value;
    setSearchQuery(newSearch);
    setCurrentPage(1); // Reset to first page when searching
  };
  
  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };
  
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };
  
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show subset with current page in middle
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);
      
      // Adjust if at start or end
      if (currentPage <= 3) {
        endPage = maxPagesToShow;
      } else if (currentPage >= totalPages - 2) {
        startPage = totalPages - maxPagesToShow + 1;
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const handleMarkReviewed = async (contactId, reviewed) => {
    setProcessing(true);
    try {
      const { scannedContactAPI } = await import('../services/api');
      if (reviewed) {
        await scannedContactAPI.markAsReviewed(contactId);
      } else {
        await scannedContactAPI.markAsUnreviewed(contactId);
      }
      
      // Update state directly without page refresh
      setContacts(prevContacts => 
        prevContacts.map(contact => 
          contact._id === contactId 
            ? { ...contact, reviewed, reviewedBy: user?.email || '', reviewedAt: new Date().toISOString() }
            : contact
        )
      );
      
      toast({
        title: "Success",
        description: reviewed ? "Card marked as reviewed" : "Card marked as unreviewed",
      });
    } catch (error) {
      console.error('Error updating review status:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update review status",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = (contact) => {
    setSelectedContact(contact);
    setEditForm({
      name: contact.name || '',
      title: contact.title || '',
      company: contact.company || '',
      email: contact.email || '',
      phone: contact.phone || '',
      phone2: contact.phone2 || '',
      location: contact.location || '',
      locationAddress: contact.locationAddress || '',
      websiteName: contact.websiteName || '',
      websiteTLD: contact.websiteTLD || '',
      bio: contact.bio || '',
      linkedin: contact.linkedin || '',
      twitter: contact.twitter || '',
      github: contact.github || '',
      instagram: contact.instagram || '',
      youtube: contact.youtube || '',
      facebook: contact.facebook || '',
      whatsapp: contact.whatsapp || '',
    });
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedContact) return;
    
    setProcessing(true);
    try {
      const { scannedContactAPI } = await import('../services/api');
      
      // Update contact data (without marking as reviewed)
      await scannedContactAPI.update(selectedContact._id, editForm);
      
      // Update state directly without page refresh
      setContacts(prevContacts => 
        prevContacts.map(contact => 
          contact._id === selectedContact._id 
            ? { 
                ...contact, 
                ...editForm, 
                updatedAt: new Date().toISOString() 
              }
            : contact
        )
      );
      
      toast({
        title: "Success",
        description: "Contact updated successfully",
      });
      
      setEditDialog(false);
      setSelectedContact(null);
    } catch (error) {
      console.error('Error updating contact:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update contact",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkReviewedFromEdit = async () => {
    if (!selectedContact) return;
    
    setProcessing(true);
    try {
      const { scannedContactAPI } = await import('../services/api');
      
      // Mark as reviewed
      await scannedContactAPI.markAsReviewed(selectedContact._id);
      
      // Update state
      setContacts(prevContacts => 
        prevContacts.map(contact => 
          contact._id === selectedContact._id 
            ? { 
                ...contact, 
                reviewed: true,
                reviewedBy: user?.email || '',
                reviewedAt: new Date().toISOString()
              }
            : contact
        )
      );
      
      toast({
        title: "Success",
        description: "Contact marked as reviewed",
      });
      
      setEditDialog(false);
      setSelectedContact(null);
    } catch (error) {
      console.error('Error marking as reviewed:', error);
      toast({
        title: "Error",
        description: "Failed to mark as reviewed",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = (contact) => {
    setSelectedContact(contact);
    setDeleteDialog(true);
  };

  const handleAssign = (contact) => {
    setSelectedContact(contact);
    setAssignEmail('');
    setAssignDialog(true);
  };

  const confirmAssign = async () => {
    if (!selectedContact || !assignEmail.trim()) {
      toast({
        title: "Error",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }
    
    setAssignLoading(true);
    try {
      const { scannedContactAPI } = await import('../services/api');
      
      // Call the assign API endpoint
      await scannedContactAPI.assignToUser(selectedContact._id, assignEmail.trim());
      
      // Remove from current list since it's now assigned to another user
      setContacts(prevContacts => 
        prevContacts.filter(contact => contact._id !== selectedContact._id)
      );
      
      toast({
        title: "Success",
        description: `Contact assigned to ${assignEmail}`,
      });
      
      setAssignDialog(false);
      setSelectedContact(null);
      setAssignEmail('');
    } catch (error) {
      console.error('Error assigning contact:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to assign contact",
        variant: "destructive",
      });
    } finally {
      setAssignLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedContact) return;
    
    setProcessing(true);
    try {
      const { scannedContactAPI } = await import('../services/api');
      // Use deleteForReview which bypasses ownership check for admin/reviewer
      await scannedContactAPI.deleteForReview(selectedContact._id);
      
      // Remove from state directly without page refresh
      setContacts(prevContacts => 
        prevContacts.filter(contact => contact._id !== selectedContact._id)
      );
      
      toast({
        title: "Success",
        description: "Contact deleted successfully",
      });
      
      setDeleteDialog(false);
      setSelectedContact(null);
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete contact",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  // No filtering needed - backend only returns pending cards
  const filteredContacts = contacts;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50 py-12">
          <div className="container mx-auto px-4">
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
              <span className="ml-3 text-gray-600">Loading...</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  
  if (!hasAccess) {
    return null; // Will redirect
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50 py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent">
              Review Physical Cards
            </h1>
            <p className="text-gray-600">Review and manage all scanned business cards</p>
          </div>

          {/* Stats Card - Pending Only */}
          <div className="mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Pending Review Cards</p>
                    <p className="text-3xl font-bold text-orange-600">{totalContacts}</p>
                    <p className="text-xs text-gray-500 mt-1">Cards awaiting review from all users</p>
                  </div>
                  <div className="p-3 bg-orange-100 rounded-full">
                    <XCircle className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Input
                type="text"
                placeholder="Search cards by name, email, company..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-10 py-6 text-lg border-2 border-gray-200 focus:border-amber-400"
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <Eye className="w-5 h-5 text-gray-400" />
              </div>
            </div>
            {searchQuery && (
              <p className="text-sm text-gray-500 mt-2">
                Searching for "{searchQuery}"... Found {totalContacts} result{totalContacts !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Pagination Controls - Above table */}
          {totalPages > 1 && (
            <div className="mb-6 flex items-center justify-center gap-2 flex-wrap">
              {/* Previous Button */}
              <Button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                variant="outline"
                className="px-4 py-2 border-2 border-gray-300 hover:border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </Button>
              
              {/* Page Numbers */}
              <div className="flex items-center gap-2">
                {getPageNumbers().map((pageNum) => (
                  <Button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    className={`px-4 py-2 min-w-[40px] ${
                      currentPage === pageNum
                        ? 'bg-gradient-to-r from-amber-600 to-yellow-500 text-white border-0'
                        : 'border-2 border-gray-300 hover:border-amber-400'
                    }`}
                  >
                    {pageNum}
                  </Button>
                ))}
              </div>
              
              {/* Next Button */}
              <Button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                variant="outline"
                className="px-4 py-2 border-2 border-gray-300 hover:border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </Button>
            </div>
          )}
          
          {/* Pagination Info */}
          {totalContacts > 0 && (
            <div className="mb-4 text-center text-sm text-gray-500">
              Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalContacts)} of {totalContacts} cards
            </div>
          )}

          {/* Cards Grid - Physical Card Style (Pending Only) */}
          {filteredContacts.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-gray-500">
                  {searchQuery ? 'No pending cards found matching your search.' : 'No pending cards to review.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredContacts.map((contact) => {
                const websiteUrl = contact.websiteName && contact.websiteTLD
                  ? `https://${contact.websiteName}${contact.websiteTLD?.startsWith('.') ? '' : '.'}${contact.websiteTLD}` 
                  : contact.website;
                
                return (
                  <div key={contact._id} className="relative">
                    {/* Physical Card Display */}
                    <div className="w-full cursor-pointer transition-transform duration-200 hover:scale-[1.02]">
                      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden relative border-2 border-gray-200">
                        {/* Review Status Badge */}
                        <div className={`absolute top-0 left-0 right-0 ${contact.reviewed ? 'bg-green-500' : 'bg-orange-500'} text-white text-center py-1 text-xs font-semibold z-10`}>
                          {contact.reviewed ? '✓ Reviewed' : 'Pending Review'}
                        </div>
                        
                        <div className="relative p-3 sm:p-6 md:p-8 pt-8 sm:pt-10">
                          <div className="flex flex-row gap-3 sm:gap-6 md:gap-8">
                            {/* Left: Card Images */}
                            {(contact.frontImage || contact.backImage) && (
                              <div className="flex-shrink-0">
                                <div className="space-y-2">
                                  {contact.frontImage && (
                                    <img 
                                      src={contact.frontImage} 
                                      alt="Front" 
                                      className="w-20 h-auto sm:w-28 sm:h-auto md:w-36 md:h-auto object-contain rounded-lg border-2 border-gray-300 shadow-md"
                                    />
                                  )}
                                  {contact.backImage && (
                                    <img 
                                      src={contact.backImage} 
                                      alt="Back" 
                                      className="w-20 h-auto sm:w-28 sm:h-auto md:w-36 md:h-auto object-contain rounded-lg border-2 border-gray-300 shadow-md"
                                    />
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Right: Profile and Info */}
                            <div className="flex-1 flex flex-col justify-center gap-2 sm:gap-3 md:gap-4 min-w-0">
                              {/* Name, Title, Company */}
                              <div className="flex-1 min-w-0 space-y-0.5 sm:space-y-1">
                                <h2 className="text-lg font-bold text-gray-900 leading-tight truncate">
                                  {contact.name}
                                </h2>
                                {contact.title && (
                                  <p className="text-sm font-medium text-gray-700 truncate opacity-90">
                                    {contact.title}
                                  </p>
                                )}
                                {contact.company && (
                                  <p className="text-sm text-gray-600 truncate opacity-80">
                                    {contact.company}
                                  </p>
                                )}
                              </div>

                              {/* Contact and Social Icons */}
                              <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 flex-wrap justify-between">
                                <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 flex-wrap">
                                  {/* Contact Icons */}
                                  {contact.email && (
                                    <a href={`mailto:${contact.email}`} onClick={(e) => e.stopPropagation()} className="text-amber-600 hover:text-amber-800 transition-colors p-1 hover:bg-amber-50 rounded-lg" title={contact.email}>
                                      <Mail className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                    </a>
                                  )}
                                  {contact.phone && (
                                    <a href={`tel:${contact.phone}`} onClick={(e) => e.stopPropagation()} className="text-teal-600 hover:text-teal-800 transition-colors p-1 hover:bg-teal-50 rounded-lg" title={contact.phone}>
                                      <Phone className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                    </a>
                                  )}
                                  {websiteUrl && (
                                    <a href={websiteUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:text-blue-800 transition-colors p-1 hover:bg-blue-50 rounded-lg" title={websiteUrl}>
                                      <Globe className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                    </a>
                                  )}
                                  {contact.location && (
                                    <span onClick={(e) => e.stopPropagation()} className="text-red-600 transition-colors p-1 rounded-lg" title={contact.location}>
                                      <MapPin className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                    </span>
                                  )}
                                  
                                  {/* Separator */}
                                  {(contact.email || contact.phone || websiteUrl || contact.location) && (contact.linkedin || contact.twitter || contact.github || contact.instagram || contact.youtube || contact.facebook || contact.whatsapp) && (
                                    <div className="w-px h-5 sm:h-5 md:h-5 bg-gray-300"></div>
                                  )}
                                  
                                  {/* Social Media Icons */}
                                  {contact.linkedin && (
                                    <a href={`https://linkedin.com/in/${contact.linkedin}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-700 hover:text-blue-900 transition-colors p-1 hover:bg-blue-50 rounded-lg" title="LinkedIn">
                                      <Linkedin className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                    </a>
                                  )}
                                  {contact.twitter && (
                                    <a href={`https://twitter.com/${contact.twitter}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-sky-500 hover:text-sky-700 transition-colors p-1 hover:bg-sky-50 rounded-lg" title="Twitter">
                                      <Twitter className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                    </a>
                                  )}
                                  {contact.github && (
                                    <a href={`https://github.com/${contact.github}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-gray-800 hover:text-black transition-colors p-1 hover:bg-gray-50 rounded-lg" title="GitHub">
                                      <Github className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                    </a>
                                  )}
                                  {contact.instagram && (
                                    <a href={`https://instagram.com/${contact.instagram}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-pink-600 hover:text-pink-800 transition-colors p-1 hover:bg-pink-50 rounded-lg" title="Instagram">
                                      <Instagram className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                    </a>
                                  )}
                                  {contact.youtube && (
                                    <a href={`https://youtube.com/@${contact.youtube}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-red-600 hover:text-red-800 transition-colors p-1 hover:bg-red-50 rounded-lg" title="YouTube">
                                      <Youtube className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                    </a>
                                  )}
                                  {contact.facebook && (
                                    <a href={`https://facebook.com/${contact.facebook}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:text-blue-800 transition-colors p-1 hover:bg-blue-50 rounded-lg" title="Facebook">
                                      <Facebook className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                    </a>
                                  )}
                                  {contact.whatsapp && (
                                    <a href={`https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-green-600 hover:text-green-800 transition-colors p-1 hover:bg-green-50 rounded-lg" title="WhatsApp">
                                      <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                    </a>
                                  )}
                                </div>

                                {/* Action Icons - Right Aligned */}
                                <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 ml-auto">
                                  {/* Separator */}
                                  <div className="w-px h-5 sm:h-5 md:h-5 bg-gray-300"></div>
                                  
                                  {/* Edit Button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(contact);
                                    }}
                                    className="text-blue-600 hover:text-blue-800 transition-colors p-1 hover:bg-blue-50 rounded-lg"
                                    title="Edit Contact"
                                  >
                                    <Edit className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                  </button>
                                  
                                  {/* Delete Button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(contact);
                                    }}
                                    className="text-red-600 hover:text-red-800 transition-colors p-1 hover:bg-red-50 rounded-lg"
                                    title="Delete Contact"
                                  >
                                    <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                  </button>
                                  
                                  {/* 3-Dots Menu (Admin only) */}
                                  {isAdmin && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <button
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-gray-600 hover:text-gray-800 transition-colors p-1 hover:bg-gray-50 rounded-lg"
                                          title="More Options"
                                        >
                                          <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                        </button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAssign(contact);
                                          }}
                                        >
                                          <UserPlus className="w-4 h-4 mr-2" />
                                          Assign to User
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Review Info Footer */}
                          {contact.reviewed && contact.reviewedBy && (
                            <div className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
                              <div>
                                <span className="font-medium">Reviewed by:</span> {contact.reviewedBy}
                              </div>
                              {contact.reviewedAt && (
                                <div className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  <span>{new Date(contact.reviewedAt).toLocaleDateString()}</span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Created Date */}
                          <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>Created: {new Date(contact.createdAt).toLocaleDateString()}</span>
                          </div>
                          {/* Created By */}
                          {contact.createdByName && (
                            <div className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                              <User className="h-3 w-3" />
                              <span>Created by: </span>
                              {contact.createdByBioUrl ? (
                                <a 
                                  href={contact.createdByBioUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:text-blue-700 hover:underline cursor-pointer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {contact.createdByName}
                                </a>
                              ) : (
                                <span>{contact.createdByName}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl font-semibold">
              <div className="w-10 h-10 flex items-center justify-center bg-orange-50 rounded-lg">
                <Edit className="w-5 h-5 text-orange-600" />
              </div>
              Edit Scanned Contact
            </DialogTitle>
          </DialogHeader>
          
          {/* Card Images Preview */}
          {selectedContact && (selectedContact.frontImage || selectedContact.backImage) && (
            <div className="mb-4 space-y-3 p-6">
              {selectedContact.frontImage && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Front Image</p>
                  <img 
                    src={selectedContact.frontImage} 
                    alt="Card front" 
                    className="w-full h-48 object-contain rounded-lg border-2 border-gray-200 bg-gray-50"
                  />
                </div>
              )}
              {selectedContact.backImage && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Back Image</p>
                  <img 
                    src={selectedContact.backImage} 
                    alt="Card back" 
                    className="w-full h-48 object-contain rounded-lg border-2 border-gray-200 bg-gray-50"
                  />
                </div>
              )}
            </div>
          )}
          
          <div className="space-y-4 p-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={editForm.title || ''}
                  onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <input
                type="text"
                value={editForm.company || ''}
                onChange={(e) => setEditForm({...editForm, company: e.target.value})}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={editForm.phone || ''}
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone 2</label>
                <input
                  type="tel"
                  value={editForm.phone2 || ''}
                  onChange={(e) => setEditForm({...editForm, phone2: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  placeholder="Secondary phone number"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={editForm.location || ''}
                  onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  placeholder="City, Country"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={editForm.locationAddress || ''}
                onChange={(e) => setEditForm({...editForm, locationAddress: e.target.value})}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website Name</label>
                <input
                  type="text"
                  value={editForm.websiteName || ''}
                  onChange={(e) => setEditForm({...editForm, websiteName: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  placeholder="example"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website TLD</label>
                <input
                  type="text"
                  value={editForm.websiteTLD || ''}
                  onChange={(e) => setEditForm({...editForm, websiteTLD: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  placeholder=".com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
              <Textarea
                value={editForm.bio || ''}
                onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                rows={3}
                placeholder="Professional bio or description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
              <input
                type="text"
                value={editForm.linkedin || ''}
                onChange={(e) => setEditForm({...editForm, linkedin: e.target.value})}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                placeholder="username"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Twitter</label>
                <input
                  type="text"
                  value={editForm.twitter || ''}
                  onChange={(e) => setEditForm({...editForm, twitter: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  placeholder="handle"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                <input
                  type="text"
                  value={editForm.instagram || ''}
                  onChange={(e) => setEditForm({...editForm, instagram: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  placeholder="handle"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
                <input
                  type="text"
                  value={editForm.facebook || ''}
                  onChange={(e) => setEditForm({...editForm, facebook: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  placeholder="username"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">GitHub</label>
                <input
                  type="text"
                  value={editForm.github || ''}
                  onChange={(e) => setEditForm({...editForm, github: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  placeholder="username"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">YouTube</label>
                <input
                  type="text"
                  value={editForm.youtube || ''}
                  onChange={(e) => setEditForm({...editForm, youtube: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  placeholder="channel"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                <input
                  type="text"
                  value={editForm.whatsapp || ''}
                  onChange={(e) => setEditForm({...editForm, whatsapp: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  placeholder="phone number"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center px-6 pb-6">
            <Button 
              variant="outline" 
              onClick={() => setEditDialog(false)}
              className="px-6 py-2"
            >
              Cancel
            </Button>
            
            <div className="flex gap-3">
              {selectedContact && !selectedContact.reviewed && (
                <Button 
                  onClick={handleMarkReviewedFromEdit}
                  disabled={processing}
                  className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {processing ? 'Marking...' : 'Mark as Reviewed'}
                </Button>
              )}
              <Button 
                onClick={handleSaveEdit}
                disabled={processing}
                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-semibold"
              >
                {processing ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Assign Contact to User
            </DialogTitle>
            <DialogDescription>
              Enter the email address of the user you want to assign this contact to.
            </DialogDescription>
          </DialogHeader>
          
          {selectedContact && (
            <div className="py-4 space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900">{selectedContact.name}</p>
                {selectedContact.company && (
                  <p className="text-sm text-gray-600">{selectedContact.company}</p>
                )}
                {selectedContact.email && (
                  <p className="text-xs text-gray-500 mt-1">{selectedContact.email}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="assignEmail" className="text-sm font-medium">
                  User Email Address *
                </Label>
                <Input
                  id="assignEmail"
                  type="email"
                  placeholder="user@example.com"
                  value={assignEmail}
                  onChange={(e) => setAssignEmail(e.target.value)}
                  className="mt-1"
                  disabled={assignLoading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  The contact will be transferred to this user's account
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setAssignDialog(false)}
              disabled={assignLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmAssign}
              disabled={assignLoading || !assignEmail.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {assignLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign Contact
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Contact</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this contact? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedContact && (
            <div className="py-4">
              <p className="font-medium">{selectedContact.name}</p>
              {selectedContact.company && (
                <p className="text-sm text-gray-600">{selectedContact.company}</p>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              disabled={processing}
            >
              {processing ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ReviewCards;
